<template>
  <div class="py-4">
    <!-- Error State -->
    <div
      v-if="store.error"
      class="mb-4 p-4 bg-red-100 text-red-700 rounded-lg"
      role="alert"
      aria-live="assertive"
    >
      {{ store.error }}
    </div>

    <!-- Skeleton Loading State: show until first equipment fetch completes -->
    <div v-if="!store.hasCompletedEquipmentFetch || store.isLoadingEquipments" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5">
      <SkeletonCard v-for="i in 10" :key="`skeleton-${i}`" />
    </div>

    <!-- Equipment Grid (only after first fetch confirmed) -->
    <div v-else>
      <!-- Preview mode: Trending → Categories → Recently Listed -->
      <template v-if="isPreview && store.equipments.length > 0">

        <!-- ━━ TRENDING ITEMS ━━ -->
        <template v-if="trendingItems.length > 0">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-xl sm:text-2xl font-bold text-theme-text flex items-center gap-2">
                <i class="pi pi-bolt text-primary-600"></i> Trending Now
              </h2>
              <p class="text-sm text-theme-muted mt-0.5">Most popular items right now</p>
            </div>
            <router-link
              to="/trending"
              class="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-500 transition-colors"
            >
              View all <i class="pi pi-arrow-right text-xs"></i>
            </router-link>
          </div>

          <div
            class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5"
            role="list"
            aria-label="Trending items"
          >
            <div
              v-for="equipment in trendingItems"
              :key="'trending-' + equipment.id"
              class="equipment-card bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
              role="listitem"
              :aria-label="`Equipment: ${equipment.name}`"
            >
              <div v-if="equipment.is_featured || equipment.is_trending" class="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1">
                <span v-if="equipment.is_trending" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-primary-600 text-white shadow"><i class="pi pi-bolt text-[9px]"></i> Trending</span>
                <span v-if="equipment.is_featured" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-neutral-900 text-secondary-500 border border-secondary-500 shadow"><i class="pi pi-star-fill text-[9px]"></i> Featured</span>
              </div>
              <div @click="goToDetail(equipment.id)" class="relative w-full aspect-[4/3] overflow-hidden bg-theme-bg2">
                <img v-if="equipment.images?.length" :src="getFullImageUrl(equipment.images[0].image_url)" :alt="equipment.name" class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                <div v-else class="absolute inset-0 flex items-center justify-center"><i class="pi pi-image text-4xl text-theme-muted"></i></div>
                <span v-if="!equipment.is_available || equipment.available_quantity === 0" class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white">Unavailable</span>
              </div>
              <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
                <div class="flex items-center gap-1 sm:gap-1.5">
                  <img v-if="equipment.owner?.image_url" :src="equipment.owner.image_url" :alt="getOwnerName(equipment)" class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border" />
                  <div v-else class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600">{{ getOwnerInitial(equipment) }}</div>
                  <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ getOwnerName(equipment) }}</span>
                  <i v-if="equipment.owner?.is_verified" class="pi pi-verified text-xs sm:text-sm text-primary-500" title="Verified"></i>
                </div>
                <h3 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug">{{ store.truncateText(equipment.name, 20) }}</h3>
                <div class="flex items-center justify-between text-[11px] sm:text-sm">
                  <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
                    <span class="text-yellow-500 hidden sm:inline">{{ renderStars(equipment.rating) }}</span>
                    <span class="text-yellow-500 sm:hidden">★</span>
                    <span class="text-theme-text2 truncate">{{ (equipment.rating || 0).toFixed(1) }} ({{ equipment.equipment_reviews?.length || 0 }})</span>
                  </div>
                  <span v-if="getDistance(equipment)" class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1"><i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>{{ getDistance(equipment) }}</span>
                </div>
                <div class="mt-auto">
                  <div class="flex items-center gap-1 mb-1">
                    <button v-for="tier in [{key:'day',label:'Day'},{key:'week',label:'Week'},{key:'month',label:'Month'}]" :key="tier.key" @click.stop="setActiveRate(equipment.id, tier.key)" class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors" :class="getActiveRate(equipment.id) === tier.key ? 'bg-primary-500 text-white' : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'">{{ tier.label }}</button>
                  </div>
                  <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">{{ formatPrice(getCurrentRate(equipment)) }}<span class="text-theme-muted text-[10px] sm:text-xs font-normal"> / {{ getRatePeriod(equipment.id) }}</span></p>
                </div>
                <button @click="goToDetail(equipment.id)" class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600" aria-label="Rent this item">View Details</button>
              </div>
            </div>
          </div>
        </template>

        <!-- ━━ CATEGORIES ━━ -->
        <HomeCategories />

        <!-- ━━ RECENTLY LISTED ━━ -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-xl sm:text-2xl font-bold text-theme-text">Recently Listed</h2>
            <p class="text-sm text-theme-muted mt-0.5">Discover items available near you</p>
          </div>
          <router-link
            to="/browse"
            class="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-500 transition-colors"
          >
            View all <i class="pi pi-arrow-right text-xs"></i>
          </router-link>
        </div>

        <div
          v-if="recentlyListedItems.length"
          class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5"
          role="list"
          aria-label="Recently listed items"
        >
          <div
            v-for="equipment in recentlyListedItems"
            :key="'recent-' + equipment.id"
            class="equipment-card bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
            role="listitem"
            :aria-label="`Equipment: ${equipment.name}`"
          >
            <div v-if="equipment.is_featured || equipment.is_trending" class="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1">
              <span v-if="equipment.is_trending" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-primary-600 text-white shadow"><i class="pi pi-bolt text-[9px]"></i> Trending</span>
              <span v-if="equipment.is_featured" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-neutral-900 text-secondary-500 border border-secondary-500 shadow"><i class="pi pi-star-fill text-[9px]"></i> Featured</span>
            </div>
            <div @click="goToDetail(equipment.id)" class="relative w-full aspect-[4/3] overflow-hidden bg-theme-bg2">
              <img v-if="equipment.images?.length" :src="getFullImageUrl(equipment.images[0].image_url)" :alt="equipment.name" class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
              <div v-else class="absolute inset-0 flex items-center justify-center"><i class="pi pi-image text-4xl text-theme-muted"></i></div>
              <span v-if="!equipment.is_available || equipment.available_quantity === 0" class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white">Unavailable</span>
            </div>
            <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
              <div class="flex items-center gap-1 sm:gap-1.5">
                <img v-if="equipment.owner?.image_url" :src="equipment.owner.image_url" :alt="getOwnerName(equipment)" class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border" />
                <div v-else class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600">{{ getOwnerInitial(equipment) }}</div>
                <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ getOwnerName(equipment) }}</span>
                <i v-if="equipment.owner?.is_verified" class="pi pi-verified text-xs sm:text-sm text-primary-500" title="Verified"></i>
              </div>
              <h3 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug">{{ store.truncateText(equipment.name, 20) }}</h3>
              <div class="flex items-center justify-between text-[11px] sm:text-sm">
                <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
                  <span class="text-yellow-500 hidden sm:inline">{{ renderStars(equipment.rating) }}</span>
                  <span class="text-yellow-500 sm:hidden">★</span>
                  <span class="text-theme-text2 truncate">{{ (equipment.rating || 0).toFixed(1) }} ({{ equipment.equipment_reviews?.length || 0 }})</span>
                </div>
                <span v-if="getDistance(equipment)" class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1"><i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>{{ getDistance(equipment) }}</span>
              </div>
              <div class="mt-auto">
                <div class="flex items-center gap-1 mb-1">
                  <button v-for="tier in [{key:'day',label:'Day'},{key:'week',label:'Week'},{key:'month',label:'Month'}]" :key="tier.key" @click.stop="setActiveRate(equipment.id, tier.key)" class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors" :class="getActiveRate(equipment.id) === tier.key ? 'bg-primary-500 text-white' : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'">{{ tier.label }}</button>
                </div>
                <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">{{ formatPrice(getCurrentRate(equipment)) }}<span class="text-theme-muted text-[10px] sm:text-xs font-normal"> / {{ getRatePeriod(equipment.id) }}</span></p>
              </div>
              <button @click="goToDetail(equipment.id)" class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600" aria-label="Rent this item">View Details</button>
            </div>
          </div>
        </div>

        <!-- Browse All CTA -->
        <div class="flex justify-center mt-8">
          <router-link
            to="/browse"
            class="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-semibold text-sm transition-all duration-300"
          >
            Browse All Items <i class="pi pi-arrow-right text-xs"></i>
          </router-link>
        </div>
      </template>

      <!-- Non-preview mode: single grid -->
      <template v-else>
      <div
        v-if="store.equipments.length > 0"
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5"
        role="list"
        aria-label="List of items"
      >
        <div
          v-for="equipment in displayedEquipments"
          :key="equipment.id"
          class="bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
          role="listitem"
          :aria-label="`Equipment: ${equipment.name}`"
        >
          <!-- Badges -->
          <div v-if="equipment.is_featured || equipment.is_trending" class="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1">
            <span v-if="equipment.is_trending" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-primary-600 text-white shadow">
              <i class="pi pi-bolt text-[9px]"></i> Trending
            </span>
            <span v-if="equipment.is_featured" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-neutral-900 text-secondary-500 border border-secondary-500 shadow">
              <i class="pi pi-star-fill text-[9px]"></i> Featured
            </span>
          </div>

          <!-- Distance badge -->

          <!-- Image -->
          <div @click="goToDetail(equipment.id)" class="relative w-full aspect-[4/3] overflow-hidden bg-theme-bg2">
            <img
              v-if="equipment.images?.length"
              :src="getFullImageUrl(equipment.images[0].image_url)"
              :alt="equipment.name"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            <div v-else class="absolute inset-0 flex items-center justify-center">
              <i class="pi pi-image text-4xl text-theme-muted"></i>
            </div>
            <!-- Availability pill -->
            <span
              v-if="!equipment.is_available || equipment.available_quantity === 0"
              class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white"
            >
              Unavailable
            </span>
          </div>

          <!-- Content -->
          <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
            <!-- Owner row -->
            <div class="flex items-center gap-1 sm:gap-1.5">
              <img
                v-if="equipment.owner?.image_url"
                :src="equipment.owner.image_url"
                :alt="getOwnerName(equipment)"
                class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border"
              />
              <div v-else class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600">
                {{ getOwnerInitial(equipment) }}
              </div>
              <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ getOwnerName(equipment) }}</span>
              <i v-if="equipment.owner?.is_verified" class="pi pi-verified text-xs sm:text-sm text-primary-500" title="Verified"></i>
            </div>

            <!-- Item name -->
            <h3 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug">
              {{ store.truncateText(equipment.name, 20) }}
            </h3>

            <!-- Rating + Distance row -->
            <div class="flex items-center justify-between text-[11px] sm:text-sm">
              <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
                <span class="text-yellow-500 hidden sm:inline">{{ renderStars(equipment.rating) }}</span>
                <span class="text-yellow-500 sm:hidden">★</span>
                <span class="text-theme-text2 truncate">{{ (equipment.rating || 0).toFixed(1) }} ({{ equipment.equipment_reviews?.length || 0 }})</span>
              </div>
              <span v-if="getDistance(equipment)" class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1">
                <i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>
                {{ getDistance(equipment) }}
              </span>
            </div>

            <!-- Pricing tiers -->
            <div class="mt-auto">
              <div class="flex items-center gap-1 mb-1">
                <button
                  v-for="tier in [{key:'day',label:'Day'},{key:'week',label:'Week'},{key:'month',label:'Month'}]"
                  :key="tier.key"
                  @click.stop="setActiveRate(equipment.id, tier.key)"
                  class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors"
                  :class="getActiveRate(equipment.id) === tier.key
                    ? 'bg-primary-500 text-white'
                    : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'"
                >
                  {{ tier.label }}
                </button>
              </div>
              <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">
                {{ formatPrice(getCurrentRate(equipment)) }}
                <span class="text-theme-muted text-[10px] sm:text-xs font-normal">/ {{ getRatePeriod(equipment.id) }}</span>
              </p>
            </div>

            <!-- CTA -->
            <button
              @click="goToDetail(equipment.id)"
              class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600"
              aria-label="Rent this item"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
      </template>

      <!-- Empty List Message -->
      <div
        v-if="!isPreview && store.equipments.length === 0 && !store.error"
        class="flex flex-col items-center justify-center py-20 text-center px-4"
        aria-live="polite"
      >
        <div
          class="flex items-center justify-center w-16 h-16 rounded-full bg-theme-bg2 shadow"
        >
          <i class="pi pi-inbox text-3xl text-theme-muted"></i>
        </div>
        <h2 class="mt-4 text-xl sm:text-2xl font-semibold text-theme-text2">
          Nothing Here Yet
        </h2>
        <p class="mt-2 text-sm sm:text-base text-theme-muted max-w-sm">
          Looks like there are no items at the moment. Start by adding one to
          get things going.
        </p>
        <button
          @click="$router.push('/list-item')"
          class="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-secondary-500 hover:bg-yellow-500 text-white font-medium shadow transition"
        >
          <i class="pi pi-plus-circle text-sm"></i>
          Add Your First Item
        </button>
      </div>
    </div>

    <!-- Full Pagination Controls (non-preview mode) -->
    <div
      v-if="!isPreview && store.totalPages > 1 && !store.isLoadingEquipments && store.equipments.length > 0"
      class="pagination flex justify-center mt-6"
      aria-label="Pagination"
    >
      <button
        :disabled="!store.previousPageUrl"
        @click="store.fetchPreviousPage"
        class="px-3 sm:px-4 py-1 sm:py-2 mx-1 bg-neutral-200 rounded-lg hover:bg-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
      >
        Previous
      </button>

      <button
        v-for="page in store.pageLinks"
        :key="page.url"
        @click="store.fetchPage(page.url)"
        class="px-3 sm:px-4 py-1 sm:py-2 mx-1 rounded-lg text-xs sm:text-sm"
        :class="page.page === store.currentPage
          ? 'bg-neutral-900 text-white'
          : 'bg-secondary-500 hover:bg-yellow-500 hover:text-white'"
      >
        {{ page.page }}
      </button>

      <button
        :disabled="!store.nextPageUrl"
        @click="store.fetchNextPage"
        class="px-3 sm:px-4 py-1 sm:py-2 mx-1 bg-neutral-200 rounded-lg hover:bg-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
      >
        Next
      </button>
    </div>
  </div>
</template>


<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { RouterLink } from 'vue-router';
import { useEquipmentsStore } from '@/store/equipments';
import HomeCategories from './HomeCategories.vue';
import SkeletonCard from './SkeletonCard.vue';
import { useCurrency } from '@/composables/useCurrency';
import { useLocation } from '@/composables/useLocation';
import { resolveImageUrl } from '@/composables/useImageUrl';

const { formatPrice } = useCurrency();
const { getDistance } = useLocation();
const getFullImageUrl = resolveImageUrl;

const props = defineProps({
  isPreview: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const store = useEquipmentsStore();

// Rate tier state per card
const activeRates = reactive({});
const getActiveRate = (id) => activeRates[id] || 'day';
const setActiveRate = (id, tier) => { activeRates[id] = tier; };
const getCurrentRate = (eq) => {
  const tier = getActiveRate(eq.id);
  if (tier === 'week') return eq.weekly_rate ?? eq.hourly_rate * 7 * 0.9;
  if (tier === 'month') return eq.monthly_rate ?? eq.hourly_rate * 30 * 0.8;
  return eq.hourly_rate;
};
const getRatePeriod = (id) => {
  const t = getActiveRate(id);
  return t === 'week' ? 'Week' : t === 'month' ? 'Month' : 'Day';
};

// Owner helpers
const getOwnerName = (eq) => {
  const o = eq.owner;
  if (!o) return 'Unknown';
  if (o.first_name || o.last_name) return `${o.first_name || ''} ${o.last_name || ''}`.trim();
  if (o.company_name) return o.company_name;
  return 'Unknown';
};
const getOwnerInitial = (eq) => getOwnerName(eq).charAt(0).toUpperCase();

// Limit items shown in preview mode (home page) to 10
const displayedEquipments = computed(() => {
  if (props.isPreview) return store.equipments.slice(0, 10);
  return store.equipments;
});

// Trending items (up to 5) for home preview
const trendingItems = computed(() =>
  store.equipments.filter(e => e.is_trending).slice(0, 5)
);

// Recently listed: exclude trending items shown above, take up to 5
const recentlyListedItems = computed(() => {
  const trendingIds = new Set(trendingItems.value.map(e => e.id));
  return store.equipments.filter(e => !trendingIds.has(e.id)).slice(0, 5);
});

// Fetch categories (+ equipments for home preview) on mount
onMounted(async () => {
  try {
    if (props.isPreview) {
      store.pageSize = 40;
      await store.fetchEquipments();
    }
    await store.fetchCategories();
  } catch (error) {
    // Store handles notifications via showNotification
  }
});

// Navigation to equipment details
const goToDetail = (equipmentId) => {
  if (equipmentId) {
    router.push({ name: 'equipment-details', params: { id: equipmentId } });
  }
};

// Render star ratings
const renderStars = (rating) => {
  const r = rating || 0;
  const full = Math.floor(r);
  const half = r % 1 >= 0.5 ? 1 : 0;
  return '★'.repeat(full) + (half ? '☆' : '') + '☆'.repeat(5 - full - half);
};
</script>

<style scoped>
.rounded {
  border-radius: 5px;
}

/* Focus styles for accessibility */
button:focus,
select:focus,
input:focus {
  outline: 2px solid var(--color-primary-400, #ff9e00);
  outline-offset: 2px;
}

/* Transition for smoother hover effects */
[class*="hover:"] {
  transition: all 0.3s ease;
}

/* Bounce animation for loading dots */
@keyframes bounce {

  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.3;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-bounce {
  animation: bounce 1.2s infinite ease-in-out;
}

.animation-delay-150 {
  animation-delay: 0.15s;
}

.animation-delay-300 {
  animation-delay: 0.3s;
}
</style>